import React from "react";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import VerticalAlignCenterIcon from "@mui/icons-material/VerticalAlignCenter";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";

import {
  LEARNING_VIDEO_MAX_UPLOAD_MB,
  uploadLearningFile,
  isVideoFile,
} from "./swipeDeckHelpers";
import { parseVideoEmbedUrl, buildVideoEmbedSrc } from "./VideoEmbed";
import { API_get_learning_file } from "../../../constants";
import { getRightworkAPI } from "../requests/utilities/requests";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import { RichTextToolbarButton, EmojiPickerButton } from "./RichTextField";

const TITLE_MAX = 60;

// Relative size steps + quick emojis — same set the image+text card editor
// uses, so the two cards offer identical text options.
const TEXT_SIZES = [
  { value: "small", label: "Small", short: "S" },
  { value: "medium", label: "Medium", short: "M" },
  { value: "large", label: "Large", short: "L" },
];
const QUICK_EMOJIS = [
  "💡", "🎉", "🎊", "🥳", "🏆", "🎁", "⭐", "❓", "🔥", "✅", "🎯", "🚀",
  "💪", "👏", "🤝", "💯", "❤️", "👀", "📣", "🚨", "⏰", "☕", "🍕", "🍔", "🍩", "🥤",
];

// How often to poll for normalization to finish (server transcodes the upload
// to a web-compatible mp4 on a background worker).
const VIDEO_STATUS_POLL_MS = 4000;

// Swipe-card videos are micro-learning clips. Keep in sync with the backend
// MAX_SWIPE_CARD_VIDEO_DURATION_SECONDS (lms_info normalize task). Checked
// client-side for instant feedback; the server enforces it authoritatively.
const SWIPE_CARD_VIDEO_MAX_DURATION_SECONDS = 10 * 60;

// Two signed S3 URLs point at the same object iff their paths match — the
// query string is just the (rotating) signature. Used to tell a genuine file
// swap (normalized mp4 → new key) from a mere re-sign, so we don't mark the
// card edited on every mount.
function _sameStoragePath(a, b) {
  try {
    return new URL(a).pathname === new URL(b).pathname;
  } catch {
    return a === b;
  }
}

// Read a video file's duration without uploading. Resolves null when the
// browser can't read it (some containers) — then we let the server enforce
// the limit rather than blocking a possibly-valid upload.
function getVideoDurationSeconds(file) {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve(Number.isFinite(v.duration) ? v.duration : null);
      };
      v.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      v.src = url;
    } catch {
      resolve(null);
    }
  });
}

const TEXT_ALIGNMENTS = [
  { value: "left", label: "Left", Icon: FormatAlignLeftIcon },
  { value: "center", label: "Center", Icon: FormatAlignCenterIcon },
  { value: "right", label: "Right", Icon: FormatAlignRightIcon },
];

const TEXT_POSITIONS = [
  { value: "top", label: "Top", Icon: VerticalAlignTopIcon },
  { value: "middle", label: "Middle", Icon: VerticalAlignCenterIcon },
  { value: "bottom", label: "Bottom", Icon: VerticalAlignBottomIcon },
];

// Video layouts mirror the image+text card, minus text_only (a video card
// with no video is meaningless). full_bleed crops a landscape clip to fill;
// split shows the whole frame in its native aspect with text below.
const LAYOUTS = [
  {
    value: "full_bleed",
    label: "Full bleed",
    description: "Video fills card, text overlay (crops landscape)",
  },
  {
    value: "split",
    label: "Split",
    description: "Whole video on top, text below (no cropping)",
  },
];

// Embeds can't be cropped (the provider player fits the video inside the frame),
// so the layout copy differs: full-bleed fills the card (great for vertical /
// Shorts, letterboxes landscape), split is a 16:9 player with text below.
const EMBED_LAYOUT_DESCRIPTIONS = {
  full_bleed: "Fills the card (best for vertical / Shorts)",
  split: "16:9 player, text below (best for landscape)",
};

/**
 * Right-pane editor for video cards. Uploaded videos are normalized
 * server-side to a web-compatible H.264 mp4 (so iPhone HEVC `.mov` plays
 * outside Safari); while that runs we show an "optimizing…" state and poll
 * until it's ready, then refresh the preview to the normalized file. The
 * backend only gets the UUID, never bytes from this component
 * (uploadLearningFile handles the presigned-PUT to S3).
 */
export function SwipeCardVideoEditor({ card, onChange, companyId }) {
  const content = card.content_json || {};
  const title = content.title || "";
  const caption = content.caption || "";
  const textAlign = content.text_align || "left";
  const textPosition = content.text_position || "bottom";
  const videoUrl = card.media_url || null;
  // Typography options (parity with the image+text card).
  const emoji = content.emoji || null;
  const titleSize = content.title_size || "medium";
  const captionSize = content.caption_size || "medium";
  const titleBold = content.title_bold !== false; // default bold
  const titleItalic = content.title_italic === true;

  const [uploadingVideo, setUploadingVideo] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);
  // null | "pending" | "processing" | "ready" | "failed"
  const [videoStatus, setVideoStatus] = React.useState(null);
  // Text in the "paste a YouTube/Vimeo link" field (only shown before a clip
  // or embed is chosen).
  const [embedInput, setEmbedInput] = React.useState("");

  // A video card is EITHER an uploaded clip OR a YouTube/Vimeo embed (mutually
  // exclusive). Embeds store only a provider + validated id in content_json;
  // the player reconstructs the iframe src from a fixed template.
  const embedProvider = content.embed_provider || "";
  const embedVideoId = content.embed_video_id || "";
  const isEmbed = Boolean(embedProvider && embedVideoId);
  const embedSrc = isEmbed
    ? buildVideoEmbedSrc({
        provider: embedProvider,
        videoId: embedVideoId,
        hash: content.embed_hash || "",
        startSeconds: content.embed_start_seconds || 0,
      })
    : null;
  // Embeds default to "split" (16:9 player + text), best for the common
  // landscape case; uploaded clips default to full_bleed (TikTok crop).
  const layout = card.layout_variant || (isEmbed ? "split" : "full_bleed");

  // Title input / caption textarea refs — for inline emoji insertion at caret.
  const titleInputRef = React.useRef(null);
  const captionInputRef = React.useRef(null);

  // Latest card, so the async poll's "ready" handler merges onto current
  // edits (title/caption the manager may have changed mid-optimization)
  // instead of a stale snapshot captured when the poll started.
  const cardRef = React.useRef(card);
  cardRef.current = card;

  // Async video work (mount-derive + poll) finishes long after the effect
  // that started it captured its closures. The editor is keyed by _clientId,
  // so it is NOT remounted on a reorder — but RouteCardEditor then passes a
  // NEW onChange closing over the card's NEW index. Calling the captured
  // onChange would write to whatever card now sits at the OLD index. Always
  // go through the latest onChange via this ref so completion mutates the
  // card this editor currently represents, regardless of reorder.
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  // On mount (incl. remount on card switch / deck reopen), derive the video
  // status from the backend. videoStatus starts null, so without this the
  // poll guard below never fires for a card whose normalization was still
  // running (or finished) while this editor was unmounted — and the session
  // keeps a stale media_url that 404s once the normalizer swaps the file and
  // deletes the original key. Runs once: the component is keyed by
  // card._clientId so it remounts per card, and an in-card upload sets the
  // status itself via handleVideoPick.
  React.useEffect(() => {
    const uuid = cardRef.current.media_file_uuid;
    if (!uuid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await getRightworkAPI(API_get_learning_file, {
          company_id: companyId,
          learning_file_uuid: uuid,
        });
        const lf = res?.data?.redux?.payload?.learning_file;
        const status = lf?.video_status || null;
        if (cancelled || !status) return;
        if (status === "pending" || status === "processing") {
          setVideoStatus(status); // wakes the poll effect below
        } else if (status === "failed") {
          setVideoStatus("failed");
          onChangeRef.current({
            ...cardRef.current,
            media_file_uuid: null,
            media_url: null,
          });
        } else if (status === "ready" && lf?.file) {
          // Refresh only when the underlying object actually changed (the
          // normalized mp4 has a new key); a re-signed URL of the same key
          // has a different signature but identical path, so skip it to avoid
          // a spurious edit. Path comparison ignores the signing query string.
          const current = cardRef.current.media_url;
          if (!current || !_sameStoragePath(current, lf.file)) {
            onChangeRef.current({ ...cardRef.current, media_url: lf.file });
          }
        }
      } catch (e) {
        // Best-effort; a save/refetch reconciles if this fails.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateContent = (patch) => {
    onChange({
      ...card,
      content_json: { ...content, ...patch },
    });
  };

  // Inline emoji insertion at the caret of the title input / caption textarea
  // (mirrors the image+text headline inserter). selectionStart/End survive the
  // field losing focus to the popover, so they point at the last caret placed.
  const insertEmojiInto = (ref, field, value, em) => {
    const input = ref.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    updateContent({ [field]: value.slice(0, start) + em + value.slice(end) });
    requestAnimationFrame(() => {
      if (!input) return;
      const pos = start + em.length;
      input.focus();
      input.setSelectionRange(pos, pos);
    });
  };
  const insertEmojiIntoTitle = (em) =>
    insertEmojiInto(titleInputRef, "title", title, em);
  const insertEmojiIntoCaption = (em) =>
    insertEmojiInto(captionInputRef, "caption", caption, em);

  // Poll normalization to completion. When ready, refresh media_url to the
  // normalized mp4 (the original key gets cleaned up server-side, so the
  // pre-normalization preview URL would otherwise go stale).
  const mediaUuid = card.media_file_uuid;
  React.useEffect(() => {
    if (videoStatus !== "pending" && videoStatus !== "processing") return undefined;
    if (!mediaUuid) return undefined;

    let cancelled = false;
    const tick = async () => {
      try {
        const res = await getRightworkAPI(API_get_learning_file, {
          company_id: companyId,
          learning_file_uuid: mediaUuid,
        });
        const lf = res?.data?.redux?.payload?.learning_file;
        const status = lf?.video_status || null;
        if (cancelled) return;
        if (status === "ready") {
          setVideoStatus("ready");
          if (lf?.file) {
            onChangeRef.current({ ...cardRef.current, media_url: lf.file });
          }
        } else if (status === "failed") {
          setVideoStatus("failed");
          // Normalization failed (over the 10-min limit or unsupported): the
          // un-normalized original won't play cross-browser and the server
          // rejects it on save. Drop it from the card so it isn't submitted
          // and the author is prompted for a different clip.
          onChangeRef.current({
            ...cardRef.current,
            media_file_uuid: null,
            media_url: null,
          });
        }
      } catch (e) {
        // transient — keep polling
      }
    };
    const id = setInterval(tick, VIDEO_STATUS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [videoStatus, mediaUuid, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVideoPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!isVideoFile(file)) {
      setUploadError("Please use an MP4, MOV, M4V, or WEBM video.");
      return;
    }
    if (file.size > LEARNING_VIDEO_MAX_UPLOAD_MB * 1024 * 1024) {
      setUploadError(
        `Video must be under ${LEARNING_VIDEO_MAX_UPLOAD_MB}MB.`,
      );
      return;
    }
    // Instant feedback on over-length clips (the server enforces this too).
    // Null = unreadable duration; let the upload through and let the server
    // decide rather than blocking a possibly-valid file.
    const durationSeconds = await getVideoDurationSeconds(file);
    if (
      durationSeconds != null &&
      durationSeconds > SWIPE_CARD_VIDEO_MAX_DURATION_SECONDS
    ) {
      setUploadError("Swipe-card videos must be under 10 minutes.");
      return;
    }
    setUploadingVideo(true);
    setUploadError(null);
    try {
      const { uuid, url, videoStatus: vs } = await uploadLearningFile(
        companyId,
        file,
      );
      // Merge onto the LATEST card via the latest onChange: title/caption stay
      // editable during the (potentially long) upload, and the card may even
      // be reordered — using ...card / a captured onChange here would clobber
      // edits or write to the wrong index. Mirrors the poll/mount-derive.
      onChangeRef.current({
        ...cardRef.current,
        media_file_uuid: uuid,
        media_url: url,
      });
      setVideoStatus(vs);
    } catch (err) {
      setUploadError(err?.message || "Upload failed. Please try again.");
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleClearVideo = () => {
    setVideoStatus(null);
    onChange({ ...card, media_file_uuid: null, media_url: null });
  };

  const applyEmbedUrl = () => {
    const parsed = parseVideoEmbedUrl(embedInput);
    if (!parsed) {
      setUploadError("Enter a valid YouTube link.");
      return;
    }
    setUploadError(null);
    setVideoStatus(null);
    // Embed and uploaded clip are mutually exclusive — clear any media refs.
    // Default embeds to "split" (the upload default is full_bleed, which
    // letterboxes a landscape embed); the author can switch to full_bleed for
    // a vertical / Shorts video.
    onChange({
      ...card,
      media_file_uuid: null,
      media_url: null,
      layout_variant: "split",
      content_json: {
        ...content,
        embed_provider: parsed.provider,
        embed_video_id: parsed.videoId,
        embed_hash: parsed.hash || "",
        embed_start_seconds: parsed.startSeconds || 0,
      },
    });
    setEmbedInput("");
  };

  const handleClearEmbed = () => {
    const next = { ...content };
    delete next.embed_provider;
    delete next.embed_video_id;
    delete next.embed_hash;
    delete next.embed_start_seconds;
    onChange({ ...card, content_json: next });
  };

  const isOptimizing =
    videoStatus === "pending" || videoStatus === "processing";

  return (
    <div className="flex flex-col gap-5 p-5">
      {/* Video upload */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
          Video
        </label>
        {isEmbed ? (
          <div className="relative rounded-lg overflow-hidden border border-slate-200 bg-black">
            <div style={{ position: "relative", width: "100%", paddingBottom: "56.25%" }}>
              <iframe
                src={embedSrc}
                title="Video embed"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
            <button
              type="button"
              onClick={handleClearEmbed}
              className="absolute top-2 right-2 px-2 py-1 rounded-md bg-black/70 text-white text-xs hover:bg-black/90"
            >
              Remove
            </button>
          </div>
        ) : videoUrl ? (
          <div className="relative rounded-lg overflow-hidden border border-slate-200 bg-black">
            <video
              src={videoUrl}
              controls
              className="w-full h-44 object-contain bg-black"
            />
            <button
              type="button"
              onClick={handleClearVideo}
              className="absolute top-2 right-2 px-2 py-1 rounded-md bg-black/70 text-white text-xs hover:bg-black/90"
            >
              Remove
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <label className="flex flex-col items-center justify-center h-32 rounded-lg border-2 border-dashed border-slate-300 hover:border-violet-500 cursor-pointer transition-colors text-sm text-slate-500">
              <span>
                {uploadingVideo ? "Uploading…" : "Click to upload a video"}
              </span>
              <span className="text-[11px] text-slate-400 mt-1">
                Any phone video (iPhone too) · under 10 min · max {LEARNING_VIDEO_MAX_UPLOAD_MB}MB
              </span>
              <input
                type="file"
                accept=".mp4,.mov,.m4v,.webm"
                onChange={handleVideoPick}
                disabled={uploadingVideo}
                className="hidden"
              />
            </label>
            {/* Or embed an existing YouTube / Vimeo video by link. */}
            <div className="flex items-center gap-2">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-[11px] uppercase tracking-wide text-slate-400">
                or paste a link
              </span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={embedInput}
                onChange={(e) => setEmbedInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    applyEmbedUrl();
                  }
                }}
                placeholder="YouTube link"
                className="flex-1 px-3 py-2 rounded-lg border border-slate-200 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-100 text-sm"
              />
              <button
                type="button"
                onClick={applyEmbedUrl}
                className="px-3 py-2 rounded-lg bg-violet-700 text-white text-sm font-medium hover:bg-violet-800 transition-colors"
              >
                Embed
              </button>
            </div>
          </div>
        )}
      </div>

      {isOptimizing && (
        <div className="rounded-lg bg-violet-50 border border-violet-200 px-3 py-2 text-xs text-violet-900">
          Optimizing this video so it plays on every device (Android, Chrome,
          iPhone). You can keep editing — this finishes in the background.
        </div>
      )}

      {videoStatus === "failed" && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
          We couldn't process this video, so it was removed. It may be over 10
          minutes or in an unsupported format. Please upload a shorter MP4 or
          MOV clip.
        </div>
      )}

      {uploadError && (
        <div className="text-xs text-red-600">{uploadError}</div>
      )}

      {/* Layout — applies to both uploaded clips and embeds. */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
          Layout
        </label>
        <div className="grid grid-cols-2 gap-2">
          {LAYOUTS.map((opt) => {
            const active = layout === opt.value;
            const description = isEmbed
              ? EMBED_LAYOUT_DESCRIPTIONS[opt.value]
              : opt.description;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange({ ...card, layout_variant: opt.value })}
                className={[
                  "rounded-lg p-3 text-left border-2 transition-colors",
                  active
                    ? "border-violet-600 bg-violet-50"
                    : "border-slate-200 hover:border-slate-300",
                ].join(" ")}
              >
                <div
                  className={[
                    "text-sm font-semibold",
                    active ? "text-violet-700" : "text-slate-700",
                  ].join(" ")}
                >
                  {opt.label}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                  {description}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Emoji prefix — a single large emoji shown above the title. */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
          Emoji prefix (optional)
        </label>
        <div className="flex items-center gap-2">
          <EmojiPickerButton
            emojis={QUICK_EMOJIS}
            selected={emoji}
            onPick={(e) => updateContent({ emoji: e })}
            onClear={() => updateContent({ emoji: null })}
            ariaLabel="Choose emoji prefix"
          />
          <span className="text-xs text-slate-400">
            {emoji ? "Shown large above the title" : "None"}
          </span>
        </div>
      </div>

      {/* Title — bold/italic toggles + inline emoji + size (mirrors the
          image+text headline; whole-field bold/italic since it's a plain
          input). */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide">
            Title (optional)
          </label>
          <span
            className={[
              "text-[11px] tabular-nums",
              title.length > TITLE_MAX
                ? "text-red-600 font-semibold"
                : "text-slate-400",
            ].join(" ")}
          >
            {title.length}/{TITLE_MAX}
          </span>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-100">
          <div className="flex items-center gap-0.5 border-b border-slate-200 px-2 py-1">
            <RichTextToolbarButton
              onClick={() => updateContent({ title_bold: !titleBold })}
              active={titleBold}
              aria-label="Bold title"
            >
              <FormatBoldIcon style={{ fontSize: 16 }} />
            </RichTextToolbarButton>
            <RichTextToolbarButton
              onClick={() => updateContent({ title_italic: !titleItalic })}
              active={titleItalic}
              aria-label="Italic title"
            >
              <FormatItalicIcon style={{ fontSize: 16 }} />
            </RichTextToolbarButton>
            <EmojiPickerButton
              emojis={QUICK_EMOJIS}
              onPick={insertEmojiIntoTitle}
            />
            <div className="ml-auto flex items-center">
              <SizePicker
                value={titleSize}
                onSelect={(v) => updateContent({ title_size: v })}
              />
            </div>
          </div>
          <input
            ref={titleInputRef}
            type="text"
            value={title}
            onChange={(e) => updateContent({ title: e.target.value })}
            placeholder="Heading shown over the video"
            className={[
              "w-full px-3 py-2 bg-transparent rounded-b-lg focus:outline-none text-sm",
              titleBold ? "font-bold" : "font-normal",
              titleItalic ? "italic" : "",
            ].join(" ")}
          />
        </div>
      </div>

      {/* Caption — inline emoji + size. */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
          Caption (optional)
        </label>
        <div className="rounded-lg border border-slate-200 bg-white focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-100">
          <div className="flex items-center gap-0.5 border-b border-slate-200 px-2 py-1">
            <EmojiPickerButton
              emojis={QUICK_EMOJIS}
              onPick={insertEmojiIntoCaption}
            />
            <div className="ml-auto flex items-center">
              <SizePicker
                value={captionSize}
                onSelect={(v) => updateContent({ caption_size: v })}
              />
            </div>
          </div>
          <textarea
            ref={captionInputRef}
            value={caption}
            onChange={(e) => updateContent({ caption: e.target.value })}
            rows={2}
            placeholder="Short supporting note shown with the title"
            className="w-full px-3 py-2 bg-transparent rounded-b-lg focus:outline-none text-sm resize-none"
          />
        </div>
      </div>

      {/* Captions tip — most employees watch muted, so captions matter.
          Soft caption upload isn't in v1, so the practical advice is:
          add captions to the video file before uploading. Not relevant to
          embeds (the provider player has its own captions). */}
      {!isEmbed && (
      <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
        <strong>Tip:</strong> Most employees watch muted. Add captions to
        your video before uploading so the message lands without sound.
      </div>
      )}

      {/* Text alignment — applies to the title + caption (overlay in full-bleed,
          the text panel in split). */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
          Text alignment
        </label>
        <div className="grid grid-cols-3 gap-2">
          {TEXT_ALIGNMENTS.map((opt) => {
            const active = textAlign === opt.value;
            const Icon = opt.Icon;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => updateContent({ text_align: opt.value })}
                className={[
                  "rounded-lg p-2 text-xs text-center border-2 transition-colors flex flex-col items-center gap-1",
                  active
                    ? "border-violet-600 bg-violet-50 text-violet-700"
                    : "border-slate-200 hover:border-slate-300 text-slate-700",
                ].join(" ")}
              >
                <Icon style={{ fontSize: 18 }} />
                <span className="font-semibold">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Text position — moves the whole title+caption block within the
          video frame. Default is bottom (the classic "subtitle" look).
          Hidden for split, where the text sits in a fixed panel below the
          video (mirrors the image+text split layout) — applies to both
          uploaded clips and embeds in full-bleed. */}
      {layout !== "split" && (
      <div>
        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
          Text position
        </label>
        <div className="grid grid-cols-3 gap-2">
          {TEXT_POSITIONS.map((opt) => {
            const active = textPosition === opt.value;
            const Icon = opt.Icon;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => updateContent({ text_position: opt.value })}
                className={[
                  "rounded-lg p-2 text-xs text-center border-2 transition-colors flex flex-col items-center gap-1",
                  active
                    ? "border-violet-600 bg-violet-50 text-violet-700"
                    : "border-slate-200 hover:border-slate-300 text-slate-700",
                ].join(" ")}
              >
                <Icon style={{ fontSize: 18 }} />
                <span className="font-semibold">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
}

/** S / M / L relative-size toggles, styled to match the B/I toolbar buttons. */
function SizePicker({ value, onSelect }) {
  return (
    <div className="flex items-center gap-0.5">
      {TEXT_SIZES.map((opt) => (
        <RichTextToolbarButton
          key={opt.value}
          onClick={() => onSelect(opt.value)}
          active={value === opt.value}
          aria-label={`${opt.label} text size`}
        >
          <span className="text-xs font-semibold">{opt.short}</span>
        </RichTextToolbarButton>
      ))}
    </div>
  );
}
